import { Injectable } from '@angular/core';
import { BaseHttpService } from '../../core/services/base-http.service';
import { HttpClient } from '@angular/common/http';
import { StorageService } from '../../core/storage/storage.service';
import { Observable } from 'rxjs';
import { config } from '../../shared/config';
import { map } from 'rxjs/operators';
import { TranslateService } from '../../core/translate/translate.service';
import { CostModuleMapper } from './cost-module.mapper';
import { ScheduleTemplateResponseDto } from '../models/schedule-template-response.dto';
import { GetOperationEventResponseDto } from '../models/get-operation-event-response.dto';
import { OperationEventResponseDto } from '../models/operation-event-response.dto';
import { FormTemplateResponseDto } from '../models/form-template-response.dto';
import { FormTemplateWithUnitIdsResponseDto } from '../models/form-template-with-unit-ids-response.dto';
import { ScheduleTemplateWithCriteriasRequestDto } from '../models/schedule-template-with-criterias-request.dto';
import { AssignScheduleToUnitRequestDto } from '../models/assign-schedule-to-unit-request.dto';
import { EditScheduleTemplateRequestDto } from '../models/edit-schedule-template-request.dto';
import { EditScheduleTemplateResponseDto } from '../models/edit-schedule-template-response.dto';
import { FormTemplateRequestDto } from '../models/form-template-request.dto';
import { EditFormTemplateRequestDto } from '../models/edit-form-template-request.dto';
import { FormTemplateWithEventIdsResponseDto } from '../models/form-template-with-event-ids-response.dto';
import { EditEventRequestDto } from '../models/edit-event-request.dto';
import { EditOperationCostRequestDto } from '../models/edit-operation-cost-request.dto';
import { OperationCostResponseDto } from '../models/operation-cost-response.dto';
import { DeleteOperationEventResponseDto } from '../models/delete-operation-event-response.dto';
import { AddOperationCostRequestDto } from '../models/add-operation-cost-response.dto';
import { AddOperationCostItemsRequestDto } from '../models/add-operation-cost-items-request.dto';
import { EditOperationCostItemsRequestDto } from '../models/edit-operation-cost-items-request.dto';
import { AddOperationEventRequestDto } from '../models/add-operation-event-request.dto';
import { UnitServiceInformationResponseDto } from '../models/unit-service-information-response.dto';
import { FormFieldTemplateResponseDto } from '../models/form-field-template-response.dto';

@Injectable({
	providedIn: 'root'
})
export class CostModuleService extends BaseHttpService {

	constructor(
		protected _http: HttpClient,
		protected _storage: StorageService,
		private _translateService: TranslateService
	) {
		super(_http, _storage);
	}

	public serviceInfo(unitIds: number[]): Observable<UnitServiceInformationResponseDto[]> {
		const fields = [ '*' ];

		const unitIdsParam = unitIds.join(',');
		const apiUrl = config.generateApiUrl(`CostModule/getServiceInfo?unitIds=${unitIdsParam}`, fields, null, '2.2');

		return this._http.get<UnitServiceInformationResponseDto>(apiUrl).pipe(
			map(response => response['data'].items)
		);
	}

	public getSchedules(): Observable<ScheduleTemplateResponseDto[]> {
		const fields = [ '*' ];

		return this._http.get(config.generateApiUrl('CostModule/getScheduleTemplates', fields, null, '2.2')).pipe(
			map(response => {
				const data = response['data'].items;
				return data.map((item: any) => ({
					...item,
					scheduleCriteria: item.scheduleCriteria.map(CostModuleMapper.mapScheduleCriteria),
					formTemplate: item.formTemplate ? {
						...item.formTemplate,
						formFieldsTemplate: item.formTemplate.formFieldsTemplate
							? item.formTemplate.formFieldsTemplate.map((field: any) => ({
								...field,
								dataType: CostModuleMapper.mapDataType(field.dataType),
								mappingTarget: CostModuleMapper.mapMappingTarget(field.mappingTarget)
							}))
							: null
					} : null
				}));
			})
		);
	}

	public getScheduleById(scheduleId: number): Observable<ScheduleTemplateResponseDto> {
		const fields = [ '*' ];

		return this._http.get(config.generateApiUrl(`CostModule/getScheduleTemplate/${scheduleId}`, fields, null, '2.2')).pipe(
			map(response => {
				console.log(response);
				const item = response['data'].item;

				if (item && item.formTemplate) {
					item.formTemplate.formFieldsTemplate = item.formTemplate.formFieldsTemplate.map((field: FormFieldTemplateResponseDto) => ({
						...field,
						dataType: CostModuleMapper.mapDataType(field.dataType),
						mappingTarget: CostModuleMapper.mapMappingTarget(field.mappingTarget)
					}));
				}
				item.scheduleCriteria = item.scheduleCriteria.map(CostModuleMapper.mapScheduleCriteria);

				return item;
			})
		);
	}

	public getEventsByUnitIds(unitIds: number[]): Observable<GetOperationEventResponseDto[]> {
		const fields = [ '*' ];
		const apiUrl = config.generateApiUrl('CostModule/getEvents', fields, null, '2.2');

		const unitIdsParam = unitIds.join(',');
		const apiUrlWithParams = `${apiUrl}&unitIds=${unitIdsParam}`;

		return this._http.get(apiUrlWithParams).pipe(
			map(response => {
				const data = response['data'].items;
				return data.map((item: GetOperationEventResponseDto) => ({
					...item,
					eventStatus: this.getStatusText(item.eventStatus),
				}));
			}));
	}

	public getEventById(eventId: number): Observable<OperationEventResponseDto> {
		const fields = [ '*' ];

		return this._http.get(config.generateApiUrl(`CostModule/getEvent/${eventId}`, fields, null, '2.2')).pipe(
			map(response => {
				const item = response['data'].item;

				if (item && item.formTemplate) {
					item.formTemplate.formFieldsTemplate = item.formTemplate.formFieldsTemplate.map((field: FormFieldTemplateResponseDto) => ({
						...field,
						dataType: CostModuleMapper.mapDataType(field.dataType),
						mappingTarget: CostModuleMapper.mapMappingTarget(field.mappingTarget)
					}));
				}
				if (item && item.schedule) {
					item.schedule = CostModuleMapper.mapSchedule(item.schedule);
				}
				item.eventStatus = this.getStatusText(item.eventStatus);

				return item;
			})
		);
	}

	public getActiveFormTemplatesWithFieldsByClientId(): Observable<FormTemplateResponseDto[]> {
		const fields = [ '*' ];

		return this.get(config.generateApiUrl('CostModule/getFormTemplates', fields, null, '2.2')).pipe(
			map((response) => {
				const items = response['data'].items;

				return items.map((item: any) => ({
					...item,
					formFields: item.formFields.map((field: FormFieldTemplateResponseDto) => ({
						...field,
						dataType: CostModuleMapper.mapDataType(field.dataType),
						mappingTarget: CostModuleMapper.mapMappingTarget(field.mappingTarget)
					}))
				}));

			})
		);
	}

	public getFormTemplateWithFieldsByByFormTemplateId(formTemplateId: number): Observable<FormTemplateWithUnitIdsResponseDto> {
		const fields = [ '*' ];

		return this._http.get(config.generateApiUrl(`CostModule/getFormTemplate/${formTemplateId}`, fields, null, '2.2')).pipe(
			map(response => {
				const item = response['data'].item;

				if (item && item.formFields) {
					item.formFields = item.formFields.map(field => ({
						...field,
						dataType: CostModuleMapper.mapDataType(field.dataType),
						mappingTarget: CostModuleMapper.mapMappingTarget(field.mappingTarget)
					}));
				}
				return item;
			})
		);
	}

	public getFormTemplateWithEventIds(): Observable<FormTemplateWithEventIdsResponseDto[]> {
		const fields = [ '*' ];

		return this._http.get<FormTemplateWithEventIdsResponseDto>(config.generateApiUrl('CostModule/getFormTempletWithEventIds', fields, null, '2.2')).pipe(
			map(response => response['data'].items)
		);
	}

	public addCostToEvent(request: AddOperationCostRequestDto): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];

		return this._http.post<OperationCostResponseDto>(config.generateApiUrl('CostModule/addCostToEvent', fields, null, '2.2'), request).pipe(
			map(response => response['data'].item)
		);
	}

	public addOperationCostItems(request: AddOperationCostItemsRequestDto): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];

		return this._http.post<OperationCostResponseDto>(config.generateApiUrl('CostModule/addOperationCostItemsAndRecalculateEventCost', fields, null, '2.2'), request);
	}

	public addEventToUnit(request: AddOperationEventRequestDto): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];

		return this._http.post<OperationCostResponseDto>(config.generateApiUrl('CostModule/addEventToUnit', fields, null, '2.2'), request).pipe(
			map(response => response['data'].item)
		);
	}

	public createScheduleTemplate(request: ScheduleTemplateWithCriteriasRequestDto): Observable<ScheduleTemplateResponseDto> {
		const fields = [ '*' ];

		return this._http.post<ScheduleTemplateResponseDto>(config.generateApiUrl('CostModule/createScheduleTemplateWithCriterias', fields, null, '2.2'), request);
	}

	public createFormTemplate(request: FormTemplateRequestDto): Observable<FormTemplateResponseDto> {
		const fields = [ '*' ];

		return this._http.post<FormTemplateResponseDto>(config.generateApiUrl('CostModule/createFormTemplate', fields, null, '2.2'), request);
	}

	public assignUnitToSchedule(request: AssignScheduleToUnitRequestDto): Observable<OperationEventResponseDto> {
		const fields = [ '*' ];

		return this._http.post<OperationEventResponseDto>(config.generateApiUrl(`CostModule/assignScheduleToUnit`, fields, null, '2.2'), request);
	}

	public editScheduleTemplate(request: EditScheduleTemplateRequestDto): Observable<EditScheduleTemplateResponseDto> {
		const fields = [ '*' ];

		return this._http.put<EditScheduleTemplateResponseDto>(config.generateApiUrl('CostModule/editScheduleTemplate', fields, null, '2.2'), request);
	}

	public editFormTemplate(request: EditFormTemplateRequestDto): Observable<FormTemplateResponseDto> {
		const fields = [ '*' ];

		return this._http.put<FormTemplateResponseDto>(config.generateApiUrl('CostModule/editFormTemplate', fields, null, '2.2'), request);
	}

	public editOperationCostItems(request: EditOperationCostItemsRequestDto): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];

		return this._http.put<OperationCostResponseDto>(config.generateApiUrl('CostModule/editOperationCostItemsAndRecalculateEventCost', fields, null, '2.2'), request);
	}

	public editEvent(request: EditEventRequestDto): Observable<OperationEventResponseDto> {
		const fields = [ '*' ];

		return this._http.put<OperationEventResponseDto>(config.generateApiUrl('CostModule/editEvent', fields, null, '2.2'), request);
	}

	public editOperationCost(request: EditOperationCostRequestDto): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];

		return this._http.put<OperationCostResponseDto>(config.generateApiUrl('CostModule/editOperationCost', fields, null, '2.2'), request);
	}

	public setDeleteEventFormTemplateStatus(formTemplateId: number): Observable<FormTemplateResponseDto> {
		const fields = [ '*' ];
		const url = config.generateApiUrl(`CostModule/setDeleteEventFormTemplateStatus?formTemplateId=${formTemplateId}`, fields, null, '2.2');

		return this._http.put<FormTemplateResponseDto>(url, null);
	}

	public setEventStatusToFinischedAndReschedule(eventId: number): Observable<OperationEventResponseDto> {
		const fields = [ '*' ];
		const url = config.generateApiUrl(`CostModule/setEventStatusToFinischedAndReschedule?eventId=${eventId}`, fields, null, '2.2');

		return this._http.put<OperationEventResponseDto>(url, null);
	}

	public setEventStatusToInProgress(eventId: number): Observable<OperationEventResponseDto> {
		const fields = [ '*' ];
		const url = config.generateApiUrl(`CostModule/setEventStatusToInProgress?eventId=${eventId}`, fields, null, '2.2');

		return this._http.put<OperationEventResponseDto>(url, null);
	}

	public deleteOperationEvent(eventId: number): Observable<DeleteOperationEventResponseDto> {
		const fields = [ '*' ];
		const url = config.generateApiUrl(`CostModule/deleteOperationEvent/${eventId}`, fields, null, '2.2');

		return this._http.delete<DeleteOperationEventResponseDto>(url);
	}

	public deleteOperationCost(eventId: number): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];
		const url = config.generateApiUrl(`CostModule/deleteOperationCost/costs/${eventId}`, fields, null, '2.2');

		return this._http.delete<OperationCostResponseDto>(url);
	}

	public deleteOperationCostItems(operationCostId: number, operationCostItemIds: number[]): Observable<OperationCostResponseDto> {
		const fields = [ '*' ];

		const operationCostItemIdsParam = operationCostItemIds.join(',');
		const apiUrl = config.generateApiUrl(`CostModule/deleteOperationCostItemsAndRecalculateEventCost/${operationCostId}`, fields, null, '2.2');

		const apiUrlWithParams = `${apiUrl}&operationCostItemIds=${operationCostItemIdsParam}`;

		return this._http.delete<OperationCostResponseDto>(apiUrlWithParams);
	}

	public setDeleteEventScheduleTemplateStatus(scheduleTemplateId: number): Observable<ScheduleTemplateResponseDto> {
		const fields = [ '*' ];
		const url = config.generateApiUrl(`CostModule/setDeleteEventScheduleTemplateStatus?formTemplateId=${scheduleTemplateId}`, fields, null, '2.2');

		return this._http.put<ScheduleTemplateResponseDto>(url, null);
	}

	private getStatusText(status: number): string {
		return CostModuleMapper.getStatusText(status, this._translateService);
	}
}
